/** @sfdc-extension-file SFDC_EXT_BRD_LOGIN_SIDEBAR */
'use client';

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import { routes } from '@/route-paths';

/** Ignore open requests briefly after logout so the guest account icon doesn't reopen the sidebar. */
const SUPPRESS_OPEN_MS = 1000;

/**
 * Steps of the header login/register sidebar flow. The sidebar is a small state
 * machine; `initialStep` seeds where it opens (e.g. the decision panel from the
 * account icon, or straight to the email step from a "Sign in" / "Join us" link).
 */
export type AuthStep =
    | 'decision'
    | 'login-email'
    | 'login-password'
    | 'forgot-password'
    | 'register-email'
    | 'register-form'
    | 'guest-checkout';

/** The steps a caller may request the sidebar to open at. */
export type AuthEntryStep = Extract<AuthStep, 'decision' | 'login-email' | 'register-email'>;

/** How the sidebar was opened — checkout mode shows the Guest Checkout path. */
export type AuthSheetMode = 'default' | 'checkout';

export type OpenAuthSheetOptions = {
    mode?: AuthSheetMode;
    /**
     * Relative path to land on after a successful login/register.
     * When `mode` is `'checkout'` and this is omitted, defaults to `routes.checkout`.
     */
    returnUrl?: string;
};

type AuthSheetProviderValue = {
    authSheetOpen: boolean;
    /** Step the panel should seed from when it (re)opens. */
    initialStep: AuthEntryStep;
    /** Entry context — checkout mode adds the Guest Checkout CTA on the decision step. */
    mode: AuthSheetMode;
    /** Post-auth redirect target when opened from cart/checkout. */
    returnUrl: string | null;
    setAuthSheetOpen: (open: boolean) => void;
    /** Open the sidebar at a given entry step (defaults to the decision panel). */
    openAuthSheet: (step?: AuthEntryStep, options?: OpenAuthSheetOptions) => void;
    closeAuthSheet: () => void;
    /**
     * Close the sidebar and ignore subsequent `openAuthSheet` calls briefly.
     * Used on logout so the account-icon click/focus race does not reopen the drawer.
     */
    suppressAuthSheetOpen: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional no-op used only when consumed outside the provider
const noop = (): void => {};

const fallbackValue: AuthSheetProviderValue = {
    authSheetOpen: false,
    initialStep: 'decision',
    mode: 'default',
    returnUrl: null,
    setAuthSheetOpen: noop,
    openAuthSheet: noop,
    closeAuthSheet: noop,
    suppressAuthSheetOpen: noop,
};

const AuthSheetContext = createContext<AuthSheetProviderValue | undefined>(undefined);

/**
 * Lightweight provider that owns only the login/register sidebar open state and
 * its entry step. Kept separate from heavier contexts (auth, basket) so opening
 * or closing the drawer never invalidates unrelated consumers.
 */
export function AuthSheetProvider({ children }: PropsWithChildren): ReactElement {
    const [authSheetOpen, setAuthSheetOpenState] = useState(false);
    const [initialStep, setInitialStep] = useState<AuthEntryStep>('decision');
    const [mode, setMode] = useState<AuthSheetMode>('default');
    const [returnUrl, setReturnUrl] = useState<string | null>(null);
    const suppressOpenUntilRef = useRef(0);

    const resetOptions = useCallback(() => {
        setMode('default');
        setReturnUrl(null);
    }, []);

    const setAuthSheetOpen = useCallback(
        (open: boolean) => {
            setAuthSheetOpenState(open);
            if (!open) {
                resetOptions();
            }
        },
        [resetOptions]
    );

    const openAuthSheet = useCallback((step: AuthEntryStep = 'decision', options?: OpenAuthSheetOptions) => {
        if (Date.now() < suppressOpenUntilRef.current) {
            return;
        }
        const nextMode = options?.mode ?? 'default';
        setInitialStep(step);
        setMode(nextMode);
        // Checkout gateway always has a post-success target — default to /checkout.
        setReturnUrl(options?.returnUrl ?? (nextMode === 'checkout' ? routes.checkout : null));
        setAuthSheetOpenState(true);
    }, []);

    const closeAuthSheet = useCallback(() => {
        setAuthSheetOpenState(false);
        resetOptions();
    }, [resetOptions]);

    const suppressAuthSheetOpen = useCallback(() => {
        suppressOpenUntilRef.current = Date.now() + SUPPRESS_OPEN_MS;
        setAuthSheetOpenState(false);
        resetOptions();
    }, [resetOptions]);

    const value = useMemo(
        () => ({
            authSheetOpen,
            initialStep,
            mode,
            returnUrl,
            setAuthSheetOpen,
            openAuthSheet,
            closeAuthSheet,
            suppressAuthSheetOpen,
        }),
        [
            authSheetOpen,
            initialStep,
            mode,
            returnUrl,
            setAuthSheetOpen,
            openAuthSheet,
            closeAuthSheet,
            suppressAuthSheetOpen,
        ]
    );

    return <AuthSheetContext.Provider value={value}>{children}</AuthSheetContext.Provider>;
}

/** Access the login/register sidebar state. Safe no-op fallback outside the provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthSheet(): AuthSheetProviderValue {
    return useContext(AuthSheetContext) ?? fallbackValue;
}

export default AuthSheetProvider;
